import { Alert, Button, Stack, TextField, Typography } from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import {
  INTAKE_CONFIRMATION_REASON_MAX,
  INTAKE_CONFIRMATION_REASON_MIN,
  intakeCheckKey,
  type IntakeCheckFinding,
} from '@munifocus/contracts-gov';
import type { Focusable } from '../../lib/useSubmitValidation';

/** The officer's answer to one finding, keyed by {@link intakeCheckKey}. */
export interface IntakeCheckAnswer {
  reason: string;
  confirmed: boolean;
}

/**
 * The Stage 1 checks the intake's SNB records raise, each with its way out.
 *
 * A finding is answered by fixing the intake — uploading the missing Parcel
 * record, changing the local government, removing the PID — at which point it
 * leaves this list on its own, or by confirming it with a reason. The reason is
 * what the file and its audit trail keep, so it is typed here rather than
 * picked: "not part of this matter" is a decision someone has to be able to
 * explain later.
 */
export function IntakeChecks({
  findings,
  answers,
  onAnswer,
  pan,
  panPids,
  localGovernmentName,
  error,
  inputRef,
}: {
  findings: IntakeCheckFinding[];
  answers: Record<string, IntakeCheckAnswer>;
  onAnswer: (key: string, answer: IntakeCheckAnswer) => void;
  pan: string;
  panPids: string[];
  localGovernmentName: string;
  /** Set by the form after a submit that left a finding unanswered. */
  error?: string;
  /** Receives the first unanswered reason field, so the submit gate can focus it. */
  inputRef?: (el: Focusable | null) => void;
}) {
  const { t } = useTranslation();
  if (findings.length === 0) return null;
  let refGiven = false;

  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      {findings.map((finding) => {
        const key = intakeCheckKey(finding);
        const answer = answers[key] ?? { reason: '', confirmed: false };
        const reasonOk = answer.reason.trim().length >= INTAKE_CONFIRMATION_REASON_MIN;
        const isMissing = finding.kind === 'pid_not_in_matter';
        const takeRef = !answer.confirmed && !refGiven;
        if (takeRef) refGiven = true;

        return (
          <Alert
            key={key}
            severity={answer.confirmed ? 'success' : 'warning'}
            // The message slot hugs its text by default, which left each
            // reason field as wide as its own sentence.
            sx={{ '& .MuiAlert-message': { flex: 1 } }}
          >
            <Stack spacing={1}>
              <Typography variant="body2">
                {isMissing
                  ? t('gov.intakeChecks.missingParcel', {
                      pan: pan || '—',
                      pids: panPids.join(', '),
                      pid: finding.pid,
                    })
                  : t('gov.intakeChecks.outsideLocalGovernment', {
                      pid: finding.pid,
                      place: finding.parcelPlace ?? t('gov.intakeChecks.unknownPlace'),
                      localGovernment: localGovernmentName,
                    })}
              </Typography>

              {answer.confirmed ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    {t('gov.intakeChecks.confirmedWith', { reason: answer.reason.trim() })}
                  </Typography>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={() => onAnswer(key, { ...answer, confirmed: false })}
                  >
                    {t('gov.intakeChecks.undo')}
                  </Button>
                </Stack>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {isMissing
                      ? t('gov.intakeChecks.missingParcelHint', { pid: finding.pid })
                      : t('gov.intakeChecks.outsideLocalGovernmentHint')}
                  </Typography>
                  <TextField
                    size="small"
                    multiline
                    minRows={1}
                    fullWidth
                    label={t('gov.intakeChecks.reason')}
                    value={answer.reason}
                    onChange={(e) => onAnswer(key, { reason: e.target.value, confirmed: false })}
                    inputRef={takeRef ? inputRef : undefined}
                    error={takeRef && Boolean(error)}
                    helperText={takeRef ? error : undefined}
                    slotProps={{ htmlInput: { maxLength: INTAKE_CONFIRMATION_REASON_MAX } }}
                  />
                  <Stack direction="row">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={!reasonOk}
                      onClick={() => onAnswer(key, { ...answer, confirmed: true })}
                    >
                      {isMissing
                        ? t('gov.intakeChecks.confirmNotInMatter')
                        : t('gov.intakeChecks.confirmBelongs')}
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </Alert>
        );
      })}
    </Stack>
  );
}
